import { Button, IconButton, Typography } from "@suraasa/placebo-ui"
import { Question, UserResponse } from "api/resources/assessments/types"
import clsx from "clsx"
import { Xmark } from "iconoir-react"
import React, { useState } from "react"

const BLANK_SEPARATOR = "__blank__"

type Props = {
  question: Question
  onChange: (questionId: string, value: UserResponse) => void
}

/**
 * Splits the question text on the blank separator.
 * `"A __blank__ B __blank__"` -> `["A ", " B ", ""]`
 * Number of blanks is always `segments.length - 1`.
 */
function getSegments(questionText: string) {
  return questionText.split(BLANK_SEPARATOR)
}

/**
 * Builds the local answer array from the response saved on the question.
 * The saved response can be `[]` (parent clears it when everything is null)
 * or shorter/longer than the number of blanks, so we normalise it here.
 */
function buildAnswers(response: UserResponse | null, blankCount: number) {
  const answers: UserResponse = new Array(blankCount).fill(null)
  response?.forEach((value, index) => {
    if (index < blankCount) answers[index] = value ?? null
  })
  return answers
}

function firstEmptyBlank(answers: UserResponse) {
  return answers.findIndex(answer => answer === null)
}

/**
 * Fill in the blanks with an "active blank" model.
 *
 * - Exactly one blank is active at a time; it is highlighted in the question text.
 * - Tapping a blank makes it active.
 * - Tapping an option fills the active blank. If that blank already had an answer,
 *   the answer is replaced and the old option goes back to the options list.
 * - After filling, the next empty blank becomes active.
 * - An answer can be removed either from the blank (× button) or by tapping the
 *   option again in the options list.
 */
function FillBlanks({ question, onChange }: Props) {
  const segments = getSegments(question.question)
  const blankCount = segments.length - 1

  const [answers, setAnswers] = useState<UserResponse>(() =>
    buildAnswers(question.response, blankCount)
  )
  const [activeBlank, setActiveBlank] = useState(() => {
    const empty = firstEmptyBlank(buildAnswers(question.response, blankCount))
    return empty === -1 ? 0 : empty
  })

  // Reset local state when we move to a different question.
  // The parent mutates the question object in place, so we can't rely on prop identity.
  const [syncedQuestionId, setSyncedQuestionId] = useState(question.id)
  if (syncedQuestionId !== question.id) {
    const newAnswers = buildAnswers(question.response, blankCount)
    const empty = firstEmptyBlank(newAnswers)
    setSyncedQuestionId(question.id)
    setAnswers(newAnswers)
    setActiveBlank(empty === -1 ? 0 : empty)
  }

  const commit = (newAnswers: UserResponse) => {
    setAnswers(newAnswers)
    onChange(question.id, newAnswers)
  }

  const clearBlank = (position: number) => {
    const newAnswers = [...answers]
    newAnswers[position] = null
    commit(newAnswers)
    setActiveBlank(position)
  }

  const selectOption = (optionKey: number) => {
    // Tapping an option that is already placed removes it from its blank.
    const usedAt = answers.indexOf(optionKey)
    if (usedAt !== -1) {
      clearBlank(usedAt)
      return
    }

    const target = activeBlank < blankCount ? activeBlank : 0
    const newAnswers = [...answers]
    newAnswers[target] = optionKey
    commit(newAnswers)

    // Move focus to the next blank that still needs an answer.
    // If everything is filled, stay on the blank we just filled so the next
    // option tap visibly replaces it instead of doing nothing.
    const nextEmpty = firstEmptyBlank(newAnswers)
    setActiveBlank(nextEmpty === -1 ? target : nextEmpty)
  }

  const renderBlank = (position: number) => {
    const answer = answers[position]
    const isActive = position === activeBlank

    if (answer === null) {
      return (
        <Button
          size="sm"
          variant="outlined"
          color={isActive ? "primary" : "black"}
          onClick={() => setActiveBlank(position)}
          aria-label={`Blank ${position + 1}, empty${
            isActive ? ", selected" : ""
          }`}
          className={clsx(
            "mx-0.5 min-w-[140px] border-dashed align-middle",
            isActive && "bg-primary-50"
          )}
        >
          {isActive ? "Pick an option" : `Blank ${position + 1}`}
        </Button>
      )
    }

    return (
      <span
        className={clsx(
          "mx-0.5 inline-flex items-center gap-0.5 rounded-lg border py-0.25 pe-0.25 ps-1 align-middle",
          isActive
            ? "border-primary bg-primary-50"
            : "border-onSurface-300 bg-surface"
        )}
      >
        <Button
          size="xs"
          variant="text"
          color="black"
          onClick={() => setActiveBlank(position)}
          aria-label={`Blank ${position + 1}, filled with ${
            question.options[answer]
          }${isActive ? ", selected" : ""}`}
        >
          {question.options[answer]}
        </Button>
        <IconButton
          size="xs"
          color="black"
          onClick={() => clearBlank(position)}
          aria-label={`Remove answer from blank ${position + 1}`}
        >
          <Xmark />
        </IconButton>
      </span>
    )
  }

  return (
    <div className="mb-2">
      <div style={{ maxWidth: "100%" }}>
        <Typography className="whitespace-pre-wrap break-words" variant="body">
          {segments.map((segment, index) => (
            <React.Fragment key={index}>
              {segment}
              {index < blankCount && renderBlank(index)}
            </React.Fragment>
          ))}
        </Typography>
      </div>

      {blankCount > 1 && (
        <Typography
          variant="smallBody"
          className="pt-1.5 text-onSurface-500"
          aria-live="polite"
        >
          Tap a blank to choose where your answer goes.
        </Typography>
      )}

      <Typography
        variant="strongSmallBody"
        className="pb-1.5 pt-2 text-onSurface-500"
      >
        Options
      </Typography>

      <div className="w-fit max-w-full sm:max-w-[80vw] md:max-w-[60vw]">
        {Object.entries(question.options).map(([key, value]) => {
          const optionKey = Number(key)
          const isSelected = answers.includes(optionKey)

          return (
            <div key={key} className="mb-2 min-w-full">
              <Button
                color="primary"
                size="md"
                variant={isSelected ? "filled" : "outlined"}
                className="w-full"
                aria-pressed={isSelected}
                onClick={() => selectOption(optionKey)}
              >
                <Typography variant="body">{value}</Typography>
              </Button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default FillBlanks
